// SITROOM Copilot — floating purple button + slide-up chat panel.
// Visible on all pages. Cmd/Ctrl+Shift+K to toggle.
//
// Iter31 rewrite: simple synchronous fetch — no SSE, no polling, no streaming.
// POST /api/copilot/chat → JSON → render full response at once.
// Greeting is a SEPARATE variable, never part of the `messages` array, so
// chips visibility is the simple invariant: `messages.length === 0`.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles, X, Send, ArrowRight } from "lucide-react";
import { useStore } from "../store";
import { useInboxCount } from "../api/sitroom";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Static row — always shown when messages is empty.
const STATIC_CHIPS = [
  { icon: "📋", label: "Open new incident", text: "Help me open a new incident" },
  { icon: "🔍", label: "Search incidents", text: "Show me all open incidents" },
  { icon: "📈", label: "Analytics summary", text: "Give me an analytics summary for the last 30 days" },
  { icon: "⚠", label: "My pending actions", text: "What tasks are waiting for my attention?" },
];

const SEV_COLOR = {
  s1: "#E24B4A",
  s2: "#f3b14a",
  s3: "#185FA5",
  s4: "#9198b0",
};

const firstName = (user) => {
  if (!user) return "there";
  const fromEmail = (user.email || "").split("@")[0].split(/[._-]/)[0];
  if (fromEmail) return fromEmail;
  const fromName = (user.name || "").trim().split(/\s+/)[0];
  return fromName || "there";
};

export const CopilotPanel = () => {
  const isCopilotOpen = useStore((s) => s.isCopilotOpen);
  const openCopilot = useStore((s) => s.openCopilot);
  const closeCopilot = useStore((s) => s.closeCopilot);
  const toggleCopilot = useStore((s) => s.toggleCopilot);
  const openPanel = useStore((s) => s.openPanel);
  const navigate = useNavigate();

  const { data: count = { unread: 0 } } = useInboxCount();
  const unread = count?.unread || 0;
  const { user } = useAuth();

  // ---------------------------------------------------------------------
  // State — `messages` starts EMPTY. Greeting is rendered separately.
  // ---------------------------------------------------------------------
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [briefing, setBriefing] = useState(null);

  const scrollRef = useRef(null);
  const taRef = useRef(null);

  // ---------------------------------------------------------------------
  // Briefing fetch — fires once per panel open, 2s timeout, silent on fail.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!isCopilotOpen) {
      setBriefing(null);
      return;
    }
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), 2000);
    axios
      .get(`${API}/briefing`, { signal: ac.signal })
      .then((r) => setBriefing(r.data || null))
      .catch(() => { /* silent — chips are best-effort */ })
      .finally(() => clearTimeout(timer));
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [isCopilotOpen]);

  // ---------------------------------------------------------------------
  // Greeting text — separate variable, NEVER in `messages`.
  // ---------------------------------------------------------------------
  const greetingText = useMemo(() => {
    const name = firstName(user);
    const base = `Hi ${name} — I'm SITREP, your situation representative.`;
    const s1n = (briefing?.s1_incidents || []).length;
    const breachn = briefing?.sla_breaches?.count || 0;
    if (s1n > 0 || breachn > 0) {
      const bits = [];
      if (s1n > 0) bits.push(`${s1n} active S1 incident${s1n === 1 ? "" : "s"}`);
      if (breachn > 0) bits.push(`${breachn} SLA breach${breachn === 1 ? "" : "es"}`);
      return `${base}\n⚠ There are ${bits.join(" and ")} right now.\nAsk me anything or pick a quick action:`;
    }
    return `${base} Ask me anything or pick a quick action:`;
  }, [user, briefing]);

  // ---------------------------------------------------------------------
  // Situational chips (max 2) from briefing.
  // ---------------------------------------------------------------------
  const situationalChips = useMemo(() => {
    if (!briefing) return [];
    const out = [];
    const s1n = (briefing.s1_incidents || []).length;
    const breachn = briefing.sla_breaches?.count || 0;
    if (s1n > 0) {
      out.push({
        icon: "🔴",
        label: `Show active S1 incident${s1n === 1 ? "" : "s"}`,
        text: "Show me the active S1 incidents",
      });
    }
    if (breachn > 0) {
      out.push({
        icon: "🚨",
        label: `Show SLA breach${breachn === 1 ? "" : "es"} (${breachn})`,
        text: "Show me incidents currently in SLA breach",
      });
    }
    return out.slice(0, 2);
  }, [briefing]);

  // The ONLY condition for chip visibility.
  const chipsVisible = messages.length === 0;

  // ---------------------------------------------------------------------
  // Global keyboard shortcut: Cmd/Ctrl+Shift+K
  // ---------------------------------------------------------------------
  useEffect(() => {
    const handler = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.shiftKey && (e.key === "K" || e.key === "k")) {
        e.preventDefault();
        toggleCopilot();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggleCopilot]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  // Auto-resize textarea
  useEffect(() => {
    if (taRef.current) {
      taRef.current.style.height = "auto";
      const next = Math.min(taRef.current.scrollHeight, 80);
      taRef.current.style.height = `${next}px`;
    }
  }, [input]);

  // ---------------------------------------------------------------------
  // sendMessage — simple synchronous fetch. No streaming, no polling.
  // ---------------------------------------------------------------------
  const sendMessage = async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || busy) return;

    const userMsg = { role: "user", content: trimmed };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setBusy(true);

    try {
      const history = next.map((m) => ({ role: m.role, content: m.content }));
      const { data } = await axios.post(
        `${API}/copilot/chat`,
        {
          messages: history,
          context: { current_page: window.location.pathname, current_incident_id: null },
        },
        { headers: { Accept: "application/json" } },
      );
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data?.response || "OK.",
          action_button: data?.action_button || null,
          results: data?.results || null,
          pending_action: data?.pending_action || null,
          pending_batch: data?.pending_batch || null,
        },
      ]);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Network error";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Sorry — ${detail}` },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const handleActionButton = (btn) => {
    if (!btn) return;
    if (btn.action === "open_incident" && btn.id) {
      openPanel(btn.id);
      navigate(`/incidents?focus=${btn.id}`);
      closeCopilot();
    } else if (btn.action === "navigate" && btn.id) {
      navigate(btn.id);
      closeCopilot();
    } else if (btn.action === "open_fmea" && btn.id) {
      navigate(`/fmea?focus=${btn.id}`);
      closeCopilot();
    } else if (btn.action === "open_tenant" && btn.id) {
      navigate(`/tenants?focus=${btn.id}`);
      closeCopilot();
    }
  };

  const handleResultClick = (kind, id) => {
    if (kind === "incident") {
      openPanel(id);
      navigate(`/incidents?focus=${id}`);
      closeCopilot();
    }
  };

  const handleConfirm = async (bubbleIdx, kind) => {
    const msg = messages[bubbleIdx];
    if (!msg) return;
    const pa = msg.pending_action;
    const pb = msg.pending_batch;
    if (kind === "single" && !pa) return;
    if (kind === "batch" && !pb) return;
    setMessages((arr) => arr.map((m, i) => (i === bubbleIdx ? { ...m, executing: true } : m)));
    try {
      const body = kind === "single"
        ? { tool: pa.tool, params: pa.params }
        : { tool: pb.tool, items: pb.items.map((it) => ({ params: it.params })), batch_summary: pb.summary };
      const { data } = await axios.post(`${API}/copilot/execute-action`, body);
      const summary = kind === "single"
        ? (data.result?.label || "Done.")
        : `Done — ${data.ok_count} ok${data.fail_count ? `, ${data.fail_count} failed` : ""}.`;
      setMessages((arr) => [
        ...arr.map((m, i) => (i === bubbleIdx ? { ...m, executing: false, pending_action: null, pending_batch: null, executed: true } : m)),
        { role: "assistant", content: `✓ ${summary}`, executed_ok: data.ok_count !== 0 || kind === "single" },
      ]);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Execution failed";
      setMessages((arr) => [
        ...arr.map((m, i) => (i === bubbleIdx ? { ...m, executing: false } : m)),
        { role: "assistant", content: `✗ ${detail}` },
      ]);
    }
  };

  const handleCancel = (bubbleIdx) => {
    setMessages((arr) => [
      ...arr.map((m, i) => (i === bubbleIdx ? { ...m, pending_action: null, pending_batch: null } : m)),
      { role: "assistant", content: "OK, cancelled." },
    ]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!busy && input.trim()) sendMessage(input);
    }
  };

  return (
    <>
      {/* Floating button — always visible */}
      <button
        onClick={openCopilot}
        className="fixed flex items-center justify-center transition-transform"
        style={{
          right: 24,
          bottom: 24,
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #6b3aa3 0%, #9b59b6 100%)",
          border: "1px solid rgba(255,255,255,0.10)",
          color: "#fff",
          cursor: "pointer",
          boxShadow: "0 8px 24px rgba(155,89,182,0.45)",
          zIndex: 240,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.06)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1.0)")}
        data-testid="copilot-fab"
        aria-label="Ask SITREP (Cmd+Shift+K)"
        title="Ask SITREP"
      >
        <Sparkles size={22} />
        {unread > 0 && (
          <span
            className="absolute"
            style={{
              top: 6,
              right: 6,
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: "#E24B4A",
              border: "2px solid #08090d",
            }}
            data-testid="copilot-fab-unread"
            data-unread={unread}
          />
        )}
      </button>

      {isCopilotOpen && (
        <div
          className="fixed flex flex-col"
          style={{
            right: 24,
            bottom: 92,
            width: 400,
            height: 560,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "calc(100vh - 110px)",
            background: "#0a0c12",
            border: "1px solid rgba(155,89,182,0.30)",
            borderRadius: 12,
            boxShadow: "0 20px 60px rgba(0,0,0,0.65)",
            zIndex: 250,
            overflow: "hidden",
            animation: "copilot-slide-up 240ms ease-out",
          }}
          data-testid="sitrep-panel"
          data-ready="true"
        >
          {/* Iter34 readiness marker — separate node for testing-agent reliability */}
          <span data-testid="sitrep-ready" style={{ display: "none" }} />
          <style>{`
            @keyframes copilot-slide-up {
              0% { opacity: 0; transform: translateY(20px); }
              100% { opacity: 1; transform: translateY(0); }
            }
          `}</style>

          {/* Header */}
          <header
            className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: "1px solid rgba(155,89,182,0.20)" }}
          >
            <div className="flex items-center gap-2">
              <Sparkles size={14} style={{ color: "#c89adb" }} />
              <span
                style={{
                  fontFamily: "Syne, sans-serif",
                  fontWeight: 600,
                  fontSize: 14,
                  color: "#f0f2f8",
                }}
              >
                ✦ SITREP
              </span>
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded"
                style={{
                  background: "rgba(155,89,182,0.20)",
                  color: "#c89adb",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 8,
                  letterSpacing: "1.2px",
                }}
              >
                CLAUDE SONNET
              </span>
            </div>
            <button
              onClick={closeCopilot}
              className="p-1 rounded"
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.10)",
                color: "#9198b0",
                cursor: "pointer",
              }}
              data-testid="copilot-close"
              aria-label="Close"
            >
              <X size={12} />
            </button>
          </header>

          {/* Messages list */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-3 py-3 space-y-3"
            data-testid="copilot-messages"
          >
            {/* Greeting bubble — rendered independently of the messages array */}
            <CopilotGreetingBubble text={greetingText} />

            {messages.map((m, i) => (
              <CopilotBubble
                key={i}
                m={m}
                idx={i}
                onActionButton={handleActionButton}
                onResultClick={handleResultClick}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
              />
            ))}

            {/* Empty state — chips visible iff messages array is empty */}
            {chipsVisible && (
              <div className="space-y-2 pt-1" data-testid="chips-container">
                {situationalChips.length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid="copilot-chips-situational">
                    {situationalChips.map((c, i) => (
                      <button
                        key={`sit-${i}`}
                        onClick={() => sendMessage(c.text)}
                        className="px-2.5 py-1 rounded-full transition-colors"
                        style={{
                          background: "rgba(226,75,74,0.10)",
                          border: "1px solid rgba(226,75,74,0.45)",
                          color: "#ff9a99",
                          fontFamily: "'DM Mono', monospace",
                          fontSize: 10,
                          cursor: "pointer",
                          letterSpacing: "0.3px",
                        }}
                        data-testid="copilot-chip-situational"
                        onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#185FA5")}
                        onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(226,75,74,0.45)")}
                      >
                        <span style={{ marginRight: 4 }}>{c.icon}</span>
                        {c.label}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-2" data-testid="copilot-chips-static">
                  {STATIC_CHIPS.map((c, i) => (
                    <button
                      key={`stat-${i}`}
                      onClick={() => sendMessage(c.text)}
                      className="px-2.5 py-1 rounded-full transition-colors"
                      style={{
                        background: "rgba(155,89,182,0.08)",
                        border: "1px solid rgba(155,89,182,0.30)",
                        color: "#c89adb",
                        fontFamily: "'DM Mono', monospace",
                        fontSize: 10,
                        cursor: "pointer",
                        letterSpacing: "0.3px",
                      }}
                      data-testid="copilot-chip-static"
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#185FA5")}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(155,89,182,0.30)")}
                    >
                      <span style={{ marginRight: 4 }}>{c.icon}</span>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {busy && <CopilotTyping />}
          </div>

          {/* Input */}
          <div
            className="px-3 py-3 flex items-end gap-2"
            style={{
              borderTop: "1px solid rgba(155,89,182,0.18)",
              background: "#0d0f18",
            }}
          >
            <textarea
              ref={taRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything…"
              disabled={busy}
              rows={1}
              className="flex-1 resize-none outline-none bg-transparent px-2 py-1.5 rounded"
              style={{
                color: "#f0f2f8",
                fontFamily: "'DM Mono', monospace",
                fontSize: 12,
                lineHeight: 1.45,
                minHeight: 28,
                maxHeight: 80,
                border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(8,9,13,0.5)",
              }}
              data-testid="copilot-input"
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={busy || !input.trim()}
              className="flex items-center justify-center rounded-md"
              style={{
                width: 32,
                height: 32,
                background: busy || !input.trim() ? "rgba(155,89,182,0.20)" : "#9b59b6",
                border: "1px solid rgba(155,89,182,0.55)",
                color: "#fff",
                cursor: busy || !input.trim() ? "not-allowed" : "pointer",
              }}
              data-testid="copilot-send"
              aria-label="Send"
            >
              <Send size={13} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const CopilotGreetingBubble = ({ text }) => (
  <div
    className="flex flex-col items-start"
    data-testid="copilot-greeting"
  >
    <div
      className="rounded-lg px-3 py-2"
      style={{
        maxWidth: "85%",
        background: "#0d0f18",
        border: "1px solid rgba(255,255,255,0.08)",
        color: "#cfd1d8",
        fontFamily: "'DM Mono', monospace",
        fontSize: 12,
        lineHeight: 1.5,
        whiteSpace: "pre-wrap",
      }}
    >
      {text}
    </div>
  </div>
);

const CopilotBubble = ({ m, idx, onActionButton, onResultClick, onConfirm, onCancel }) => {
  const isUser = m.role === "user";
  const text = m.content || "";
  const hasPending = !isUser && (m.pending_action || m.pending_batch);
  if (!text && !m.results && !hasPending) return null;
  const bubbleColor = m.executed ? "#0d1f15" : (isUser ? "#185FA5" : "#0d0f18");
  const bubbleBorder = m.executed ? "1px solid rgba(78,208,132,0.40)" : (isUser ? "1px solid #185FA5" : "1px solid rgba(255,255,255,0.08)");
  return (
    <div
      className={`flex flex-col ${isUser ? "items-end" : "items-start"}`}
      data-testid="message-bubble"
      data-role={isUser ? "user" : "assistant"}
    >
      <div
        className="rounded-lg px-3 py-2"
        style={{
          maxWidth: "85%",
          background: bubbleColor,
          border: bubbleBorder,
          color: isUser ? "#fff" : "#cfd1d8",
          fontFamily: "'DM Mono', monospace",
          fontSize: 12,
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
        }}
      >
        {text}
      </div>

      {/* Confirm card — single pending action */}
      {m.pending_action && (
        <CopilotConfirmCard
          label={m.pending_action.label || `Run ${m.pending_action.tool}`}
          executing={m.executing}
          onConfirm={() => onConfirm(idx, "single")}
          onCancel={() => onCancel(idx)}
          testid="copilot-confirm-card"
        />
      )}

      {/* Confirm card — batch */}
      {m.pending_batch && (
        <CopilotBatchConfirmCard
          batch={m.pending_batch}
          executing={m.executing}
          onConfirm={() => onConfirm(idx, "batch")}
          onCancel={() => onCancel(idx)}
        />
      )}

      {/* Result cards */}
      {m.results && !isUser && (
        <CopilotResults results={m.results} onClick={onResultClick} />
      )}

      {/* Action button */}
      {m.action_button && !isUser && (
        <button
          onClick={() => onActionButton(m.action_button)}
          className="mt-1.5 flex items-center gap-1.5 px-3 py-1.5 rounded transition-colors"
          style={{
            background: "rgba(24,95,165,0.18)",
            border: "1px solid rgba(24,95,165,0.55)",
            color: "#9ec3ec",
            fontFamily: "Syne, sans-serif",
            fontWeight: 600,
            fontSize: 11,
            letterSpacing: "0.4px",
            cursor: "pointer",
          }}
          data-testid="copilot-action-button"
        >
          {m.action_button.label || "Open"} <ArrowRight size={11} />
        </button>
      )}
    </div>
  );
};

const CopilotResults = ({ results, onClick }) => {
  if (!results || typeof results !== "object") return null;
  const incidents = Array.isArray(results.incidents) ? results.incidents : [];
  const openLoops = Array.isArray(results.open_loops) ? results.open_loops : [];
  const tenants = Array.isArray(results.tenants) ? results.tenants : [];
  const fmeas = Array.isArray(results.fmeas) ? results.fmeas : [];
  const allIncidents = [...incidents, ...openLoops];

  if (!allIncidents.length && !tenants.length && !fmeas.length) return null;

  return (
    <div className="mt-2 space-y-1.5 w-full" data-testid="copilot-results">
      {allIncidents.slice(0, 5).map((inc) => (
        <button
          key={inc.id}
          onClick={() => onClick("incident", inc.id)}
          className="w-full text-left p-2 rounded transition-colors"
          style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.08)",
            cursor: "pointer",
          }}
          data-testid={`copilot-result-incident-${inc.id}`}
        >
          <div className="flex items-center gap-2">
            <span
              className="inline-block rounded px-1.5 py-0.5 uppercase"
              style={{
                background: `${SEV_COLOR[inc.severity] || "#9198b0"}22`,
                color: SEV_COLOR[inc.severity] || "#9198b0",
                fontFamily: "Syne, sans-serif",
                fontWeight: 700,
                fontSize: 9,
                letterSpacing: "0.5px",
              }}
            >
              {(inc.severity || "—").toUpperCase()}
            </span>
            <span
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 10,
                color: "#9ec3ec",
              }}
            >
              {inc.id}
            </span>
            <span
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 9,
                color: "#9198b0",
                letterSpacing: "0.6px",
                textTransform: "uppercase",
                marginLeft: "auto",
              }}
            >
              {inc.status}
            </span>
          </div>
          <div
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              color: "#f0f2f8",
              marginTop: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {inc.title}
          </div>
        </button>
      ))}
      {tenants.slice(0, 5).map((t) => (
        <div
          key={t.id || t.name}
          className="w-full p-2 rounded"
          style={{
            background: "rgba(155,89,182,0.06)",
            border: "1px solid rgba(155,89,182,0.30)",
          }}
          data-testid={`copilot-result-tenant-${t.id || t.name}`}
        >
          <div
            style={{
              fontFamily: "Syne, sans-serif",
              fontWeight: 600,
              fontSize: 12,
              color: "#c89adb",
            }}
          >
            {t.name}
          </div>
          {t.type && (
            <div
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 9,
                color: "#9198b0",
                letterSpacing: "1.2px",
                textTransform: "uppercase",
                marginTop: 1,
              }}
            >
              {t.type}
            </div>
          )}
        </div>
      ))}
      {fmeas.slice(0, 5).map((f) => (
        <div
          key={f.id}
          className="w-full p-2 rounded"
          style={{
            background: "rgba(243,177,74,0.06)",
            border: "1px solid rgba(243,177,74,0.30)",
          }}
          data-testid={`copilot-result-fmea-${f.id}`}
        >
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#f3b14a" }}>
            {f.id}
          </div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#f0f2f8", marginTop: 2 }}>
            {f.title}
          </div>
        </div>
      ))}
      {results.rate_pct !== undefined && (
        <div
          className="p-2 rounded"
          style={{
            background: "rgba(24,95,165,0.08)",
            border: "1px solid rgba(24,95,165,0.30)",
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
            color: "#9ec3ec",
          }}
          data-testid="copilot-result-metric"
        >
          {results.metric}: <span style={{ color: "#f0f2f8" }}>{results.rate_pct}%</span>
          {results.total ? ` · ${results.breached || results.completed || 0}/${results.total}` : ""}
        </div>
      )}
    </div>
  );
};

const CopilotConfirmCard = ({ label, executing, onConfirm, onCancel, testid = "copilot-confirm-card" }) => (
  <div
    className="mt-2 rounded-lg p-2.5 w-full"
    style={{
      background: "rgba(155,89,182,0.06)",
      border: "1px solid rgba(155,89,182,0.40)",
    }}
    data-testid={testid}
  >
    <div
      style={{
        fontFamily: "'DM Mono', monospace",
        fontSize: 11,
        color: "#cfd1d8",
        marginBottom: 8,
      }}
      data-testid="copilot-confirm-label"
    >
      {label}
    </div>
    <div className="flex items-center gap-2">
      <button
        onClick={onConfirm}
        disabled={executing}
        className="flex items-center gap-1.5 px-3 py-1 rounded transition-colors"
        style={{
          background: executing ? "rgba(78,208,132,0.20)" : "#4ed084",
          border: "1px solid #4ed084",
          color: executing ? "#9198b0" : "#08090d",
          fontFamily: "Syne, sans-serif",
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: "0.4px",
          cursor: executing ? "not-allowed" : "pointer",
        }}
        data-testid="copilot-confirm-btn"
      >
        {executing ? "Working…" : "✓ Confirm"}
      </button>
      <button
        onClick={onCancel}
        disabled={executing}
        className="px-3 py-1 rounded transition-colors"
        style={{
          background: "transparent",
          border: "1px solid rgba(255,255,255,0.15)",
          color: "#9198b0",
          fontFamily: "Syne, sans-serif",
          fontWeight: 600,
          fontSize: 11,
          letterSpacing: "0.4px",
          cursor: executing ? "not-allowed" : "pointer",
        }}
        data-testid="copilot-cancel-btn"
      >
        ✗ Cancel
      </button>
    </div>
  </div>
);

const CopilotBatchConfirmCard = ({ batch, executing, onConfirm, onCancel }) => (
  <div
    className="mt-2 rounded-lg p-2.5 w-full"
    style={{
      background: "rgba(243,177,74,0.06)",
      border: "1px solid rgba(243,177,74,0.40)",
    }}
    data-testid="copilot-batch-confirm-card"
  >
    {batch.summary && (
      <div
        style={{
          fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12,
          color: "#f3b14a", marginBottom: 6,
        }}
        data-testid="copilot-batch-summary"
      >
        {batch.summary}
      </div>
    )}
    <div
      className="space-y-1 mb-2"
      style={{ maxHeight: 140, overflowY: "auto" }}
      data-testid="copilot-batch-items"
    >
      {batch.items.map((it, i) => (
        <div
          key={i}
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 10,
            color: "#cfd1d8",
            padding: "2px 6px",
            background: "rgba(255,255,255,0.02)",
            borderRadius: 3,
          }}
        >
          • {it.label || JSON.stringify(it.params)}
        </div>
      ))}
    </div>
    <div className="flex items-center gap-2">
      <button
        onClick={onConfirm}
        disabled={executing}
        className="flex items-center gap-1.5 px-3 py-1 rounded"
        style={{
          background: executing ? "rgba(78,208,132,0.20)" : "#4ed084",
          border: "1px solid #4ed084",
          color: executing ? "#9198b0" : "#08090d",
          fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 11,
          letterSpacing: "0.4px",
          cursor: executing ? "not-allowed" : "pointer",
        }}
        data-testid="copilot-batch-confirm-btn"
      >
        {executing ? "Working…" : `✓ Confirm all (${batch.items.length})`}
      </button>
      <button
        onClick={onCancel}
        disabled={executing}
        className="px-3 py-1 rounded"
        style={{
          background: "transparent",
          border: "1px solid rgba(255,255,255,0.15)",
          color: "#9198b0",
          fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 11,
          letterSpacing: "0.4px",
          cursor: executing ? "not-allowed" : "pointer",
        }}
        data-testid="copilot-batch-cancel-btn"
      >
        ✗ Cancel
      </button>
    </div>
  </div>
);

const CopilotTyping = () => (
  <div className="flex justify-start" data-testid="typing-indicator">
    <div
      className="rounded-lg px-3 py-2.5 flex items-center gap-1"
      style={{ background: "#0d0f18", border: "1px solid rgba(155,89,182,0.20)" }}
    >
      <span style={dotStyle(0)} />
      <span style={dotStyle(150)} />
      <span style={dotStyle(300)} />
      <style>{`
        @keyframes copilot-bounce {
          0%, 80%, 100% { opacity: 0.3; transform: translateY(0); }
          40% { opacity: 1; transform: translateY(-3px); }
        }
      `}</style>
    </div>
  </div>
);

const dotStyle = (delay) => ({
  width: 5,
  height: 5,
  borderRadius: "50%",
  background: "#c89adb",
  display: "inline-block",
  animation: "copilot-bounce 1.2s ease-in-out infinite",
  animationDelay: `${delay}ms`,
});
